/**
 * AppleStudio — the Apple pass entry point (§3.2), split off PassStudio.tsx.
 * TEMPLATES-ONLY: pick a TEMPLATE card (Utazási biztosítás / Általános
 * biztosítás) and land STRAIGHT in the editor — there is no style choice, the
 * template fixes the canvas (utazas → boardingPass, altalanos → generic, see
 * STUDIO_APPLE_STYLE). The direct-manipulation canvas (S4) AUTO-SAVES the
 * per-(template, appleStyle) design; Step 2 collects recipient + per-template
 * data (the travel flight is OPTIONAL) and mints Apple-only, rendering the
 * add-to-wallet link inline.
 *
 * The template lives in the URL (`/studio/apple/:template`, slugs
 * `utazas`|`altalanos`) rather than component state, so a bookmark or
 * back-navigation reproduces the exact same screen. The segment is untrusted:
 * an unknown template renders the template picker. Legacy deep links redirect:
 * a two-segment `/studio/apple/:template/:style` drops its style segment, and
 * a single-segment style link (`/studio/apple/<style>`, pre-template era) maps
 * style → template (generic → altalanos, anything else known → utazas).
 *
 * Step 1 is `Step1Canvas` below: it owns the `useDesignDoc` working copy and
 * renders the pass ITSELF as the editor (PassCanvas/BackOfPass + the
 * FieldInspector/AddFieldMenu/chrome/barcode popovers) — there is no separate
 * form-panel + static preview split any more (S4 replaced both the old
 * PassDesignEditor panel and the ApplePassPreview proxy with the canvas).
 *
 * The primary advance is the "Tovább a generáláshoz" button (enabled on
 * !hasBlockingError && !saving); it awaits the canvas's registered save —
 * which persists even an unedited seed (seeded → false) AND forces
 * is_active=TRUE so the mint's getActive resolves the SAME design shown on
 * the canvas — and moves to Step 2 ONLY on a CONFIRMED persist: save resolves
 * null on a 422 (the canvas stays mounted with the inline errors + working
 * copy) and rejects on network/5xx (a retry hint renders under the button).
 * The step-2 chip runs the SAME flush-confirm advance (it must not bypass the
 * confirmed-persist gate with a blind step switch), and `designSaved` is keyed
 * by the saved doc's own (templateKind, appleStyle) — so a PUT resolving after
 * a selection switch can never unlock (or advance to) Step 2 for a
 * never-persisted selection.
 */

import type { PassDesign } from '@insurwiz/wallet-core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { getPassDesign, mintPass } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { BarcodeEditor } from '../components/designer/BarcodeEditor';
import { ColorsEditor } from '../components/designer/ColorsEditor';
import { ImagesEditor } from '../components/designer/ImagesEditor';
import { FeaturedActionsEditor } from '../components/passDesigner/FeaturedActionsEditor';
import { RelevanceEditor } from '../components/passDesigner/RelevanceEditor';
import { SemanticsEditor } from '../components/passDesigner/SemanticsEditor';
import { ServiceURLsEditor } from '../components/passDesigner/ServiceURLsEditor';
import { sampleSeedFor } from '../components/passDesigner/sampleSeed';
import { AddFieldMenu } from '../components/studio/AddFieldMenu';
import { AssistanceMintForm } from '../components/studio/AssistanceMintForm';
import { AutomationMomentCard } from '../components/studio/AutomationMomentCard';
import { BackOfPass } from '../components/studio/BackOfPass';
import { FieldInspector } from '../components/studio/FieldInspector';
import { MintSuccess } from '../components/studio/MintSuccess';
import { NotificationsPanel } from '../components/studio/NotificationsPanel';
import { PassCanvas, type CanvasTarget } from '../components/studio/PassCanvas';
import { Popover } from '../components/studio/Popover';
import { StepChip } from '../components/studio/StepChip';
import { StyleGallery } from '../components/studio/StyleGallery';
import { TravelMintForm } from '../components/studio/TravelMintForm';
import {
  STUDIO_APPLE_STYLE,
  STUDIO_TEMPLATE_KINDS,
  studioTemplateCards,
  studioTemplateTitles,
  templateKindFromSlug,
  templateSlugForLegacyStyle,
  type StudioTemplateKind,
} from '../components/studio/templateCatalog';
import { useDesignDoc } from '../hooks/useDesignDoc';
import { useT } from '../i18n';
import { describeApiError } from '../lib/apiErrors';
import { designImageUrl } from '../lib/designImages';
import { setFieldProp } from '../lib/fieldEditing';
import type {
  DemoPartner,
  DemoPass,
  MintPassRequest,
  PassDesignDocDto,
  PassTemplateKind,
} from '../types';
import { APPLE_STYLES, type AppleStyle } from '../walletStyles';

export interface AppleStudioPageProps {
  /** Injectable for tests; defaults to the authed partner's enabledTemplates. */
  enabledTemplates?: PassTemplateKind[];
}

/**
 * The honest no-access stub. Template enablement is NOT operator-self-service
 * (the Megjelenés page has no such switch), so the copy points at the team
 * that can actually flip it instead of a dead-end control.
 */
function TemplateGateStub({ title, body }: { title: string; body: string }): JSX.Element {
  const t = useT();
  return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center space-y-3">
      <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
      <p className="text-sm text-slate-600">
        {body} {t('studio.gate.contactPrefix')}{' '}
        <a href="mailto:hello@insurwiz.io" className="text-brand-700 hover:underline">
          hello@insurwiz.io
        </a>
        {t('studio.gate.contactSuffix')}
      </p>
    </div>
  );
}

export function AppleStudioPage({ enabledTemplates }: AppleStudioPageProps): JSX.Element {
  // `useAuth()` throws outside <AuthProvider> (auth/AuthProvider.tsx). Component
  // tests mount this page with only <QueryClientProvider>+<MemoryRouter> and
  // inject `enabledTemplates`, so guard the read. The hook is still called
  // unconditionally (inside the try), so rules-of-hooks holds.
  let partner: DemoPartner | null = null;
  try {
    partner = useAuth().partner;
  } catch {
    partner = null; // unauthed render path — `enabledTemplates` prop drives the gate
  }
  const t = useT();
  const templateTitles = studioTemplateTitles(t);
  const allEnabled = enabledTemplates ?? partner?.enabledTemplates ?? ['travel'];
  const kinds = STUDIO_TEMPLATE_KINDS.filter((k) => allEnabled.includes(k));

  const navigate = useNavigate();
  // The template segment is untrusted: anything but a known slug
  // (utazas|altalanos) falls back to `null`, which renders the template picker
  // instead of trusting the param. There is no style dimension any more — the
  // template FIXES the canvas style (STUDIO_APPLE_STYLE); a legacy :style
  // segment is redirected away below.
  const { template: templateParam, style: styleParam } = useParams<{
    template?: string;
    style?: string;
  }>();
  const templateKind = templateKindFromSlug(templateParam);
  const appleStyle: AppleStyle | null =
    templateKind !== null ? STUDIO_APPLE_STYLE[templateKind] : null;

  const [step, setStep] = useState<1 | 2>(1);
  // The (template, style) key of the last CONFIRMED persist — designSaved
  // derives from equality with the current selection, so a PUT that resolves
  // after the operator switched selection can never enable chip 2 / Step 2 for
  // the newly selected, never-persisted design.
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [blocking, setBlocking] = useState(false);
  // A non-422 rejection (network/5xx) of the pre-advance save — surfaces a
  // retry hint under the button. 422s need no flag: the canvas renders those
  // errors inline and `blocking` disables the advance until they are fixed.
  const [saveFailed, setSaveFailed] = useState(false);
  // Resolves the persisted doc, or null when the PUT 422'd (nothing saved).
  const saveRef = useRef<(() => Promise<PassDesignDocDto | null>) | null>(null);

  const selectionKey =
    templateKind !== null && appleStyle !== null ? `${templateKind}:${appleStyle}` : null;
  const designSaved = selectionKey !== null && savedKey === selectionKey;
  // Per-render-updated (same idiom as useDesignDoc's saveFnRef) so async save
  // continuations compare against the selection the operator sees NOW, not the
  // render they started in.
  const selectionKeyRef = useRef(selectionKey);
  selectionKeyRef.current = selectionKey;

  const queryClient = useQueryClient();

  // A step switch scrolls back to the top — the advance button sits at the
  // bottom of a long editor, and Step 2 used to open mid-page.
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // jsdom has no scrollIntoView — optional call keeps tests quiet.
    topRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [step]);

  const query = useQuery<PassDesignDocDto>({
    queryKey: ['passDesign', templateKind, appleStyle],
    queryFn: () => getPassDesign(templateKind as PassTemplateKind, appleStyle as AppleStyle),
    enabled: templateKind !== null && appleStyle !== null,
  });

  // Reset cross-step state on a template switch (including the picker ↔
  // editor transitions).
  useEffect(() => {
    setSavedKey(null);
    setStep(1);
    setBlocking(false);
    setSaveFailed(false);
  }, [templateKind, appleStyle]);

  // The loaded doc (persisted row or serviceToDesign seed) — named docDto (not
  // `doc`) because Step1Canvas below names its OWN useDesignDoc() result `doc`;
  // keeping the two apart avoids a confusing shadowed name at the call site.
  const docDto = query.data;

  // Legacy two-segment deep link (`/studio/apple/:template/:style`, pre-
  // templates-only era): the style segment is IGNORED — the template alone
  // fixes the canvas, so canonicalize to the single-segment URL (an invalid
  // template then falls back to the picker there).
  if (styleParam !== undefined) {
    return <Navigate to={`/studio/apple/${templateParam ?? ''}`} replace />;
  }

  // Legacy single-segment style link (`/studio/apple/<style>`, pre-template
  // era): map the style to the template that owns its layout now
  // (generic → altalanos, anything else known → utazas). Unknown segments
  // fall through to the template picker below.
  if (templateKind === null && templateParam !== undefined) {
    const legacySlug = templateSlugForLegacyStyle(
      templateParam,
      APPLE_STYLES.map((s) => s.value),
    );
    if (legacySlug !== null) {
      return <Navigate to={`/studio/apple/${legacySlug}`} replace />;
    }
  }

  if (kinds.length === 0) {
    return <TemplateGateStub title={t('studio.apple.title')} body={t('studio.gate.none')} />;
  }

  // Template picker — /studio/apple, plus every unrecognized URL shape.
  if (templateKind === null) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('studio.apple.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t('studio.pickTemplate')}</p>
        </div>
        <StyleGallery
          options={studioTemplateCards(t).map((card) => {
            const enabled = kinds.includes(templateKindFromSlug(card.value) as StudioTemplateKind);
            return enabled
              ? card
              : { ...card, disabled: true, disabledHint: t('studio.notEnabledHint') };
          })}
          onPick={(slug) => navigate(`/studio/apple/${slug}`)}
        />
      </div>
    );
  }

  if (!kinds.includes(templateKind)) {
    return (
      <TemplateGateStub
        title={t('studio.apple.title')}
        body={t('studio.gate.template', { title: templateTitles[templateKind] })}
      />
    );
  }

  // The template fixes the style — re-derive under the narrowed templateKind
  // (identical to `appleStyle` above, but typed non-null for the JSX below).
  const fixedStyle = STUDIO_APPLE_STYLE[templateKind];

  const canAdvance = !blocking && !saving && !!docDto;

  // The ONLY route to Step 2 (button AND chip): flush-confirm the design, then
  // advance ONLY on a confirmed persist. save() resolves null on a 422 (the
  // canvas keeps the inline errors + working copy mounted and `blocking`
  // flips) and rejects on network/5xx — both keep the operator on Step 1 so
  // the mint can never target a design that differs from what they last saw.
  // The saved doc must also still match the CURRENT selection: a switch during
  // the in-flight PUT must not land the operator on Step 2 of a
  // never-persisted design.
  const advanceToStep2 = async (): Promise<void> => {
    setSaveFailed(false);
    try {
      const saved = await saveRef.current?.();
      if (saved && `${saved.templateKind}:${saved.appleStyle}` === selectionKeyRef.current) {
        setStep(2);
      }
    } catch {
      // Surface the retry hint only if the operator is still on the selection
      // whose save failed — the reset effect already cleared it on a switch,
      // and a late rejection must not resurrect it for the new selection.
      if (selectionKeyRef.current === selectionKey) setSaveFailed(true);
    }
  };

  return (
    <div ref={topRef} className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5 scroll-mt-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('studio.apple.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {t('studio.apple.subtitle', { title: templateTitles[templateKind] })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/studio/apple')}
          className="text-sm text-brand-700 hover:underline"
        >
          {t('studio.changeTemplate')}
        </button>
      </div>

      <ol className="flex items-center gap-3">
        <StepChip
          n={1}
          active={step === 1}
          done={designSaved}
          label={t('studio.step1')}
          onClick={() => setStep(1)}
        />
        <StepChip
          n={2}
          active={step === 2}
          disabled={!designSaved || blocking || saving}
          done={false}
          label={t('studio.step2')}
          onClick={() => {
            // The chip must respect the SAME confirmed-persist gate as the
            // advance button — a blind setStep(2) could carry pending edits
            // into a mint of the stale persisted design.
            if (step !== 2) void advanceToStep2();
          }}
        />
      </ol>

      {query.isError ? (
        // Honest load state: a failed GET must not sit on "Betöltés…" forever —
        // surface the failure and offer a retry (refetch) in place.
        <div className="space-y-2">
          <p className="text-sm text-red-600">{t('studio.loadFailed')}</p>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="btn-primary py-1.5 text-sm"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : query.isLoading || !docDto ? (
        <div className="text-sm text-slate-500">{t('common.loading')}</div>
      ) : (
        <div className="space-y-4">
          {step === 1 ? (
            <>
              {/* Remount on a selection switch so the canvas's working copy
                  can never be PUT under the newly-selected key. */}
              <Step1Canvas
                key={selectionKey}
                templateKind={templateKind}
                appleStyle={fixedStyle}
                initial={docDto}
                onValidityChange={setBlocking}
                onSavingChange={setSaving}
                registerSave={(fn) => {
                  saveRef.current = fn;
                }}
                onSaved={(saved) => {
                  // Mark saved-ness under the DOC's OWN key — a late-resolving
                  // auto-save PUT for a previous selection then simply fails
                  // the `designSaved` key-equality and cannot enable chip 2.
                  if (!saved.seeded) {
                    setSavedKey(`${saved.templateKind}:${saved.appleStyle}`);
                  }
                  // Reconcile the query cache with the persisted doc so a
                  // switch away-and-back within `staleTime` reads the saved
                  // state with no refetch race.
                  queryClient.setQueryData<PassDesignDocDto>(
                    ['passDesign', saved.templateKind, saved.appleStyle],
                    saved,
                  );
                }}
              >
                <div className="space-y-1">
                  <button
                    type="button"
                    disabled={!canAdvance}
                    onClick={() => void advanceToStep2()}
                    className="btn-primary w-full sm:w-auto disabled:opacity-50"
                  >
                    {t('studio.advance')}
                  </button>
                  {blocking ? (
                    <p className="text-xs text-slate-500">{t('studio.blockingHint')}</p>
                  ) : null}
                  {saveFailed ? (
                    <p className="text-xs text-red-600">{t('studio.saveFailedHint')}</p>
                  ) : null}
                </div>
              </Step1Canvas>
            </>
          ) : (
            <StudioStep2
              templateKind={templateKind}
              appleStyle={fixedStyle}
              design={docDto.design}
              defaultEmail={partner?.email ?? ''}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — the direct-manipulation canvas (S4). Owns the useDesignDoc working
// copy; renders the pass itself (front/back) as the editing surface, with
// per-target popovers for fields/chrome/barcode/add. Mirrors its
// validity/saving state and registers its pre-advance save with the host
// exactly like the old PassDesignEditor did, so AppleStudioPage's advance
// button + race guards above are untouched.
// ---------------------------------------------------------------------------

interface Step1CanvasProps {
  templateKind: StudioTemplateKind;
  appleStyle: AppleStyle;
  /** The loaded doc (persisted row or serviceToDesign seed) for this style. */
  initial: PassDesignDocDto;
  /** Fired with the server-returned doc after a successful save (see useDesignDoc). */
  onSaved: (saved: PassDesignDocDto) => void;
  /** Mirrors hasBlockingError(errors) to the host (gates the advance button). */
  onValidityChange: (blocking: boolean) => void;
  /** Mirrors the in-flight save state to the host. */
  onSavingChange: (saving: boolean) => void;
  /** Registers the hook's pre-advance save (persists isActive: true) with the host. */
  registerSave: (fn: () => Promise<PassDesignDocDto | null>) => void;
  /** The host's advance controls — rendered at the foot of the settings column. */
  children?: ReactNode;
}

function Step1Canvas({
  templateKind,
  appleStyle,
  initial,
  onSaved,
  onValidityChange,
  onSavingChange,
  registerSave,
  children,
}: Step1CanvasProps): JSX.Element {
  const t = useT();
  const doc = useDesignDoc({ templateKind, appleStyle, initial, autoSaveMs: 800, onSaved });
  const [selected, setSelected] = useState<CanvasTarget | null>(null);
  const [face, setFace] = useState<'front' | 'back'>('front');
  // "Okos funkciók" (UX4 item 5) — collapsed by default; restores the four
  // smart editors the canvas assembly otherwise never mounts.
  const [smartOpen, setSmartOpen] = useState(false);
  // The smart editors' [LIVE] locks are PRESENCE-IN-SEED based
  // (seedLiveKeys.ts), so the seed must be the kind-aware SAMPLE seed
  // (sampleSeedFor — the client mirror of the server GET-one seed path),
  // never the loaded doc: `initial` is the live react-query doc that
  // AppleStudioPage's onSaved reconciles after EVERY autosave (setQueryData),
  // and a PERSISTED row is operator-authored — deriving locks from either
  // would freeze the operator's own saved semantics/relevance values (the
  // value they just typed flips to read-only after the next 800ms autosave,
  // and a reload locks every field they ever saved). sampleSeedFor is cached
  // per (kind, style), so the seed is referentially stable across renders.
  const smartSeed = sampleSeedFor(templateKind, appleStyle);

  useEffect(() => {
    onValidityChange(doc.saveBlocked);
  }, [doc.saveBlocked, onValidityChange]);
  useEffect(() => {
    onSavingChange(doc.saving);
  }, [doc.saving, onSavingChange]);
  useEffect(() => {
    registerSave(doc.hostSave);
  }, [registerSave, doc.hostSave]);

  // The generic (altalanos) canvas is flightless by construction — no flight
  // subscription ever backs it, so every flight affordance (semantics editor,
  // delay-moment card, live flight palette, airport relevance copy) must not
  // render there. The style IS the discriminator: the template fixes it
  // (travel → boardingPass, assistance → generic).
  const flightCanvas = appleStyle === 'boardingPass';

  // Findings summary — MOUNTED-ONLY exclusion rule. A finding is left out of
  // the summary only while the editor that renders its path inline is
  // actually on screen: unlike the old panel (every section editor always
  // mounted), the canvas hosts Colors and Barcode in popovers, so their
  // paths must fall back to the summary whenever that popover is CLOSED —
  // otherwise a colors./barcode. severity:'error' would render NOWHERE while
  // still disabling Mentés/Tovább (a dead-end for the operator). The same
  // rule extends to the "Okos funkciók" section (UX4 item 5): semantics./
  // relevance.locations/serviceURLs./featuredActions are excluded ONLY while
  // it is OPEN — closed, those editors unmount and the summary is their only
  // possible home again. semantics. is excluded only on the FLIGHT canvas:
  // the generic canvas never mounts SemanticsEditor, so its findings' only
  // home is the summary even while the section is open. Everything else
  // ALWAYS shows here: FieldInspector deliberately does NOT render field
  // errors inline (a behaviour change vs the old panel's always-mounted
  // PassFieldsEditor).
  const mountedInlinePrefixes: string[] = [];
  if (selected?.kind === 'chrome') mountedInlinePrefixes.push('colors.');
  if (selected?.kind === 'barcode') mountedInlinePrefixes.push('barcode.');
  if (smartOpen) {
    mountedInlinePrefixes.push('relevance.locations', 'serviceURLs.', 'featuredActions');
    if (flightCanvas) mountedInlinePrefixes.push('semantics.');
  }
  const otherFindings = doc.errors.filter(
    (e) => !mountedInlinePrefixes.some((p) => e.path.startsWith(p)),
  );

  return (
    // data-testid alias: the canvas assembly IS the design editor — tests and
    // hosts that predate the canvas keep addressing it as pass-design-editor.
    // From lg up the editor is two columns: the pass stays in view (sticky)
    // while the notifications / smart features / save row scroll beside it —
    // one narrow column used to scroll the pass away while editing its pushes.
    <div
      data-testid="pass-design-editor"
      data-style={doc.design.style}
      className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-[360px_minmax(0,1fr)] lg:items-start lg:gap-8"
    >
      <div className="relative space-y-4 lg:sticky lg:top-6">
        {face === 'front' ? (
          <PassCanvas
            design={doc.design}
            selected={selected}
            onSelect={(t) => {
              if (t.kind === 'back') {
                // Flipping to the back hides whatever front-face region a
                // popover was targeting — close it too, or FieldInspector/
                // AddFieldMenu keeps floating over the (now hidden) canvas,
                // silently editable while the operator sees the back.
                setFace('back');
                setSelected(null);
              } else {
                setSelected(t);
              }
            }}
            onFieldValueChange={(g, i, v) =>
              doc.setDesign((prev) => setFieldProp(prev, g, i, 'value', v))
            }
            imageUrlFor={(slot) => designImageUrl(doc.design, slot)}
          />
        ) : (
          <BackOfPass
            design={doc.design}
            onChange={doc.setDesign}
            onFlipBack={() => setFace('front')}
          />
        )}

        {selected?.kind === 'field' ? (
          <FieldInspector
            design={doc.design}
            group={selected.group}
            index={selected.index}
            onChange={doc.setDesign}
            onClose={() => setSelected(null)}
            onIndexChange={(newIndex) =>
              setSelected({ kind: 'field', group: selected.group, index: newIndex })
            }
          />
        ) : null}
        {selected?.kind === 'add' ? (
          <AddFieldMenu
            design={doc.design}
            group={selected.group}
            onChange={doc.setDesign}
            onClose={() => setSelected(null)}
            // Auto-select the fresh custom field so its inspector opens and the
            // operator lands straight in the Felirat/Érték inputs.
            onAdded={(g, i) => setSelected({ kind: 'field', group: g, index: i })}
          />
        ) : null}
        {selected?.kind === 'chrome' ? (
          <Popover
            label={t('popover.appearance')}
            onClose={() => setSelected(null)}
            className="w-80"
          >
            {/* Hidden plumbing stays hidden; colors/images REUSE the existing editors verbatim. */}
            <div className="space-y-4">
              <ColorsEditor design={doc.design} errors={doc.errors} onChange={doc.applyPatch} />
              <ImagesEditor design={doc.design} onChange={doc.applyPatch} />
            </div>
          </Popover>
        ) : null}
        {selected?.kind === 'barcode' ? (
          <Popover label={t('popover.barcode')} onClose={() => setSelected(null)} className="w-80">
            <BarcodeEditor design={doc.design} errors={doc.errors} onChange={doc.applyPatch} />
          </Popover>
        ) : null}
      </div>

      <div className="space-y-4 min-w-0">
        {/* Travel: the EDITABLE push plan (spec 2026-08-31 Part B) — reminder
          timing/copy + the delay-moment copy, saved with the design through
          the same debounced useDesignDoc save. Generic: no flight, no delay
          events and no boarding sweep — the honest static card stays: the
          operator-update push (changeMessage). */}
        {flightCanvas ? (
          <NotificationsPanel
            value={doc.notifications}
            defaults={doc.notificationDefaults}
            onChange={doc.setNotifications}
          />
        ) : (
          <AutomationMomentCard variant="update" />
        )}

        {/* "Okos funkciók" (UX4 item 5) — the canvas assembly above mounts only
          Colors/Images/Barcode, so RelevanceEditor (the airport geofence),
          SemanticsEditor, ServiceURLsEditor and FeaturedActionsEditor became
          unreachable. Collapsed by default; reuses each editor VERBATIM —
          same design/errors/onChange wiring the pre-deletion PassDesignEditor
          panel used (git show 0c41161:.../PassDesignEditor.tsx). `seed` is
          the kind-aware SAMPLE seed (see smartSeed above). SemanticsEditor is
          flight-only ("Okos járat-adatok" — the iOS flight-tracking triple):
          the generic canvas has no flight, so it does not mount there. */}
        <section className="card p-4 space-y-3">
          <button
            type="button"
            aria-expanded={smartOpen}
            onClick={() => setSmartOpen((o) => !o)}
            className="flex w-full items-center justify-between text-sm font-semibold text-slate-800"
          >
            <span>{t('canvas.smartFeatures')}</span>
            <span aria-hidden="true">{smartOpen ? '▲' : '▼'}</span>
          </button>
          {smartOpen ? (
            <div className="space-y-4">
              <RelevanceEditor
                design={doc.design}
                seed={smartSeed}
                errors={doc.errors}
                onChange={doc.applyPatch}
                flightBound={flightCanvas}
              />
              {flightCanvas ? (
                <SemanticsEditor design={doc.design} seed={smartSeed} onChange={doc.applyPatch} />
              ) : null}
              <ServiceURLsEditor
                design={doc.design}
                errors={doc.errors}
                onChange={doc.applyPatch}
              />
              <FeaturedActionsEditor
                design={doc.design}
                errors={doc.errors}
                onChange={doc.applyPatch}
              />
            </div>
          ) : null}
        </section>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-4">
            <span
              className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
                doc.seeded ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-800'
              }`}
            >
              {doc.seeded ? t('canvas.unsavedBadge') : t('canvas.savedBadge')}
            </span>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                aria-label={t('canvas.activeDesign')}
                checked={doc.isActive}
                disabled={doc.seeded || doc.saving}
                onChange={doc.toggleActive}
                className="accent-brand-700"
              />
              {t('canvas.activeDesign')}
            </label>
            <button
              type="button"
              onClick={doc.save}
              disabled={doc.saveBlocked || doc.saving}
              className="btn-primary py-1.5 text-sm disabled:opacity-50"
            >
              {doc.saving ? t('canvas.saving') : t('canvas.save')}
            </button>
            {doc.saveFailed ? (
              // Honest status: a rejected PUT persisted nothing — the hook
              // reverted its optimistic seeded/isActive flips, and this badge is
              // the failure's visible home for the debounce/Mentés/toggle paths
              // (the pre-advance save ALSO gets the host's retry hint under the
              // Tovább button).
              <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-red-100 text-red-800">
                {t('canvas.saveFailedBadge')}
              </span>
            ) : null}
          </div>

          <p className="text-xs text-slate-500">{t('canvas.activeDesignHint')}</p>

          {otherFindings.length > 0 ? (
            <ul className="space-y-1">
              {otherFindings.map((e) => (
                <li
                  key={e.path + e.message}
                  className={
                    e.severity === 'error' ? 'text-xs text-red-600' : 'text-xs text-amber-700'
                  }
                >
                  {e.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — recipient + per-template data + Generate (Apple-only mint with the
// template's fixed appleStyle) + the inline add-to-wallet link. Both branches
// are the SHARED studio forms (TravelMintForm with its optional flight /
// AssistanceMintForm), which compose the shared passForms/mintPayloads
// fragments — so the surfaces stay in lock-step by construction. Only the
// transit-only testLat/testLng geofence is intentionally absent here (never
// applied to the Apple path).
// ---------------------------------------------------------------------------

interface StudioStep2Props {
  templateKind: StudioTemplateKind;
  appleStyle: AppleStyle;
  /** The saved design this mint uses — shown beside the form. */
  design: PassDesign;
  defaultEmail: string;
}

function StudioStep2({
  templateKind,
  appleStyle,
  design,
  defaultEmail,
}: StudioStep2Props): JSX.Element {
  const t = useT();
  const [result, setResult] = useState<DemoPass | null>(null);
  // Bumped by "Új pass" — remounts the form with fresh values.
  const [formKey, setFormKey] = useState(0);
  const queryClient = useQueryClient();
  const mint = useMutation({
    mutationFn: (req: MintPassRequest) => mintPass(req),
    onSuccess: (pass) => {
      // Same as every sibling mint surface — without it the Dashboard's
      // ['passes'] query (staleTime 10s / refetchInterval 30s) can miss the
      // studio-minted pass for up to ~30s.
      void queryClient.invalidateQueries({ queryKey: ['passes'] });
      setResult(pass);
    },
  });

  return (
    <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-[360px_minmax(0,1fr)] lg:items-start lg:gap-8">
      {/* The design this mint uses stays in view beside the form. */}
      <aside className="space-y-2 lg:sticky lg:top-6">
        <p className="text-xs text-slate-500">{t('studio.step2PreviewHint')}</p>
        <PassCanvas
          design={design}
          selected={null}
          onSelect={() => {}}
          imageUrlFor={(slot) => designImageUrl(design, slot)}
          readOnly
        />
      </aside>
      <div data-testid="studio-step2" className="card p-5 sm:p-6 space-y-4 min-w-0">
        {result ? (
          // The result REPLACES the form — no accidental second mint.
          <MintSuccess
            pass={result}
            onAnother={() => {
              setResult(null);
              mint.reset();
              setFormKey((k) => k + 1);
            }}
          />
        ) : (
          <>
            {templateKind === 'travel' ? (
              <TravelMintForm
                key={formKey}
                defaultEmail={defaultEmail}
                mint={mint}
                platformFields={{ offeredPlatforms: ['apple'], appleStyle }}
              />
            ) : (
              <AssistanceMintForm
                key={formKey}
                defaultEmail={defaultEmail}
                mint={mint}
                platformFields={{ offeredPlatforms: ['apple'], appleStyle }}
              />
            )}
            {mint.error ? (
              <div className="rounded-md bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800">
                {describeApiError(mint.error, t)}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
